import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { OutwriteApi } from '../services/OutwriteService';
import { countSentences, countWords } from '../outwrite/learnerPracticeUtils';
import { LEARNER } from '../outwrite/learnerTokens';
import OutwriteLearnerWords from './OutwriteLearnerWords';

const CHECK_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Write-the-overview step (mockup 05): textarea, words panel, Check.
 */
export default function OutwriteLearnerOverview({
  overviewTask,
  text,
  onTextChange,
  lexis,
  onBackToQuestions,
}) {
  const [wordsOpen, setWordsOpen] = useState(true);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const sentences = useMemo(() => countSentences(text), [text]);
  const words = useMemo(() => countWords(text), [text]);
  const prompt =
    overviewTask?.to_finds?.[0]?.prompt ||
    'Write an overview of the graph. One to three sentences. Describe the main trend – no figures.';

  const handleCheck = () => {
    const trimmed = (text || '').trim();
    if (!trimmed || !overviewTask?.id || checkLoading) return;
    setCheckLoading(true);
    setCheckResult(null);
    OutwriteApi.check(trimmed, overviewTask.id, CHECK_TIMEOUT_MS)
      .then((data) => setCheckResult(data))
      .catch(() => {})
      .finally(() => setCheckLoading(false));
  };

  const results = checkResult?.assessment_results || [];

  return (
    <Box
      component="section"
      aria-label="Write the overview"
      sx={{
        flex: '1 1 480px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: LEARNER.surface,
          border: `1px solid ${LEARNER.outlineVariant}`,
          borderRadius: '12px',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography
          component="h1"
          sx={{
            m: 0,
            fontSize: 22,
            lineHeight: '28px',
            fontWeight: 400,
          }}
        >
          Write an overview of the graph
        </Typography>
        <Typography sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}>
          {prompt}
        </Typography>

        <TextField
          label="Your overview"
          multiline
          minRows={5}
          fullWidth
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
            },
          }}
        />

        <Typography sx={{ fontSize: 12, color: LEARNER.onSurfaceVariant }}>
          Sentences: {sentences} · Words: {words}
          {' · '}
          1–3 sentences
        </Typography>

        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setWordsOpen((v) => !v)}
            sx={{
              height: 40,
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: LEARNER.secondaryContainer,
              color: LEARNER.onSecondaryContainer,
              '&:hover': { bgcolor: LEARNER.secondaryContainer },
            }}
          >
            Words for this graph
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!text?.trim() || checkLoading || !overviewTask?.id}
            onClick={handleCheck}
            startIcon={
              checkLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              height: 40,
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: LEARNER.primary,
              '&:hover': { bgcolor: '#1D4785' },
              '&.Mui-disabled': {
                bgcolor: LEARNER.disabled,
                color: LEARNER.onDisabled,
              },
            }}
          >
            Check my overview
          </Button>
        </Stack>

        {wordsOpen ? (
          <OutwriteLearnerWords
            lexis={lexis}
            variant="panel"
            onClose={() => setWordsOpen(false)}
          />
        ) : null}

        {results.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {results.map((row, i) => {
              const pass = String(row.result || '').toUpperCase() === 'PASS';
              return (
                <Box
                  key={`${row.rubric_id}-${i}`}
                  sx={{
                    borderRadius: '12px',
                    p: 1.5,
                    bgcolor: pass
                      ? LEARNER.successContainer
                      : LEARNER.errorContainer,
                    color: pass
                      ? LEARNER.onSuccessContainer
                      : LEARNER.onErrorContainer,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {row.rubric_id}: {row.result}
                  </Typography>
                  {row.note ? (
                    <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                      {row.note}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        ) : null}
      </Box>

      <Button
        variant="text"
        onClick={onBackToQuestions}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 500,
          color: LEARNER.primary,
        }}
      >
        Back to the questions
      </Button>
    </Box>
  );
}
